import BpmnRules from "bpmn-js/lib/features/rules/BpmnRules";

export default class CustomBpmnRules extends BpmnRules {
  private _bpmnFactory: any;

  constructor(eventBus, bpmnFactory) {
    super(eventBus);
    this._bpmnFactory = bpmnFactory;
    this.init();
  }

  init = () => {
    this.addRule("shape.attach", 10000, this.AdaptiveTimerAttachRule);
    this.addRule("shape.create", 10000, this.AdaptiveTimerCreateRule);
    this.addRule("element.hover", 10000, this.AdaptiveTimerHoverRule);
    this.addRule("shape.attach", 9000, this.GeneralBoundaryEventRule); // Lower priority fallback
    this.addRule("shape.attach", 8000, this.PreventMultipleBoundaryEventsRule); // Prevent any multiple boundary events
    this.addRule("element.dblclick", 10000, (context) => {
      const { element } = context;
      if (element.businessObject.$type === "bpmn:Lane" || element.businessObject.$type === "bpmn:Participant") {
        return false;
      }
    });
    // Happy Path phase labels live centred in the column's header band (positioned by
    // happyPathPhaseInterceptor) — they are the column's title, not a free-floating label, so
    // dragging one on its own is disallowed. Only blocks when EVERY moved element is a phase
    // label: moving the group itself (label follows via LabelSupport) and mixed selections stay
    // native, and the interceptor's own re-centre commands bypass rules entirely.
    this.addRule("elements.move", 10000, this.PreventPhaseLabelMoveRule);
    // Phase columns are resized ONLY via the boundary grips in their header row
    // (happyPathPhaseInterceptor) — the native 8-handle resize can't express "move the shared
    // boundary" and was fiddly on a pool-height shape. Programmatic resizes (retile, grip commit)
    // are commands and bypass rules, so they keep working.
    this.addRule("shape.resize", 10000, this.PreventPhaseGroupNativeResizeRule);
  };

  PreventPhaseGroupNativeResizeRule = (context) => {
    const bo = context.shape?.businessObject;
    if (bo?.$type === "bpmn:Group" && bo?.isHappyPathPhase === true) return false;
    return undefined;
  };

  PreventPhaseLabelMoveRule = (context) => {
    const shapes = context.shapes || [];
    if (shapes.length === 0) return undefined;
    const isPhaseLabel = (s) => {
      const targetBo = s.labelTarget?.businessObject;
      return s.type === "label" && targetBo?.$type === "bpmn:Group" && targetBo?.isHappyPathPhase === true;
    };
    if (shapes.every(isPhaseLabel)) return false;
    return undefined;
  };

  AdaptiveTimerAttachRule = (context) => {
    var target = context.target;
    var shape = context.shape;
    var targetBo = target ? target.businessObject : null;

    if (!shape.isAdaptiveTimerEvent) {
      return undefined;
    }

    if (!target || !targetBo) {
      return undefined;
    }

    if (targetBo.$type === "bpmn:Task" || targetBo.$type === "bpmn:UserTask") {
      // Check if target already has attachers (only allow one boundary event per task)
      if (target.attachers && target.attachers.length > 0) {
        return false;
      }

      this.transformToBoundaryEvent(shape);
      return this.canAttach(shape, target, context.source, context.position);
    }

    return undefined;
  };

  AdaptiveTimerCreateRule = (context) => {
    var target = context.target;
    var shape = context.shape;
    var targetBo = target ? target.businessObject : null;

    if (!shape.isAdaptiveTimerEvent) {
      return undefined;
    }

    if (target && targetBo && targetBo.$type === "bpmn:SequenceFlow") {
      this.ensureIntermediateEvent(shape);
      return undefined; // Let default rules handle sequence flow splitting
    }

    if (target && targetBo) {
      this.ensureIntermediateEvent(shape);
    }

    return undefined; // Let default rules handle
  };

  transformToBoundaryEvent = (shape) => {
    if (shape.businessObject.$type === "bpmn:BoundaryEvent") {
      return;
    }

    const oldBo = shape.businessObject;
    const boundaryEventBo = this._bpmnFactory.create("bpmn:BoundaryEvent", {
      id: oldBo.id,
      name: oldBo.name
    });

    if (oldBo.eventDefinitions) {
      boundaryEventBo.eventDefinitions = oldBo.eventDefinitions;
    }

    shape.type = "bpmn:BoundaryEvent";
    shape.businessObject = boundaryEventBo;

    shape.isAdaptiveTimerEvent = true;
  };

  ensureIntermediateEvent = (shape) => {
    if (shape.businessObject.$type === "bpmn:IntermediateCatchEvent") {
      return;
    }

    const oldBo = shape.businessObject;
    const intermediateEventBo = this._bpmnFactory.create("bpmn:IntermediateCatchEvent", {
      id: oldBo.id,
      name: oldBo.name
    });

    if (oldBo.eventDefinitions) {
      intermediateEventBo.eventDefinitions = oldBo.eventDefinitions;
    }

    shape.type = "bpmn:IntermediateCatchEvent";
    shape.businessObject = intermediateEventBo;

    shape.isAdaptiveTimerEvent = true;
  };

  AdaptiveTimerHoverRule = (context) => {
    var target = context.target;
    var shape = context.shape;

    if (!shape || !shape.isAdaptiveTimerEvent) {
      return undefined;
    }

    if (target && target.businessObject) {
      var targetBo = target.businessObject;

      if (targetBo.$type === "bpmn:Task" || targetBo.$type === "bpmn:UserTask") {
        return {
          cursor: "copy",
          message: "Will attach as boundary timer event"
        };
      }

      if (targetBo.$type === "bpmn:SequenceFlow") {
        return {
          cursor: "copy",
          message: "Will insert timer event in flow"
        };
      }
    }

    return undefined;
  };

  GeneralBoundaryEventRule = (context) => {
    var target = context.target;
    var shape = context.shape;
    var shapeBo = shape.businessObject;
    var targetBo = target ? target.businessObject : null;

    if (shapeBo.$type === "bpmn:BoundaryEvent" && !shape.isAdaptiveTimerEvent) {
      if (!targetBo || !["bpmn:Task", "bpmn:UserTask"].includes(targetBo.$type)) {
        return false;
      }

      if (target.attachers && target.attachers.length > 0) {
        return false;
      }

      return this.canAttach(shape, target, context.source, context.position);
    }

    return undefined;
  };

  PreventMultipleBoundaryEventsRule = (context) => {
    var target = context.target;
    var shape = context.shape;
    var shapeBo = shape.businessObject;
    var targetBo = target ? target.businessObject : null;

    if (
      shapeBo &&
      shapeBo.$type === "bpmn:BoundaryEvent" &&
      targetBo &&
      ["bpmn:Task", "bpmn:UserTask"].includes(targetBo.$type)
    ) {
      if (target.attachers && target.attachers.length > 0) {
        return false;
      }
    }

    return undefined; // Let other rules handle non-boundary events
  };
}

CustomBpmnRules.$inject = ["eventBus", "bpmnFactory"];
